import { useState, useEffect } from 'react';

function useQueryParam(): [string];
function useQueryParam(
  key: string,
  defaultValue?: string
): [string, (value: string) => void];
function useQueryParam(key?: string, defaultValue = '') {
  const getParamValue = (): string => {
    if (key) {
      const params = new URLSearchParams(window.location.search);
      return params.get(key) || defaultValue;
    }
    return window.location.search;
  };

  const [paramValue, setParamValue] = useState<string>(getParamValue);

  useEffect(() => {
    const updateValue = () => {
      setParamValue(getParamValue());
    };

    const patchHistoryMethod = (type: 'pushState' | 'replaceState') => {
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      const original = window.history[type] as (...args: any[]) => void;
      return function (
        this: typeof window.history,
        ...args: Parameters<typeof original>
      ) {
        const result = original.apply(this, args);
        window.dispatchEvent(new Event('urlchange'));
        return result;
      };
    };

    window.history.pushState = patchHistoryMethod('pushState');
    window.history.replaceState = patchHistoryMethod('replaceState');

    window.addEventListener('popstate', updateValue);
    window.addEventListener('urlchange', updateValue);

    return () => {
      window.removeEventListener('popstate', updateValue);
      window.removeEventListener('urlchange', updateValue);
    };
  }, [key, defaultValue]);

  const setParam = (value: string) => {
    const params = new URLSearchParams(window.location.search);
    if (key) {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    }

    const queryString = params.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
    setParamValue(value);
  };

  return [paramValue, ...(key ? [setParam] : [])];
}

export default useQueryParam;
